import clsx from 'clsx'
import PropTypes from 'prop-types'
import { useCallback, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router'
import AssessmentIllustration from '../../../assets/images/discover/assessments.png'
import EvaluationsIllustration from '../../../assets/images/discover/evaluations.png'
import Button from '../../../components/tailwind/Button'
import trackEvents from '../../../constants/track-events'
import {
  useNotificationContext,
  usePartnerContext,
  useUserContext
} from '../../../context'
import { query } from '../../../graphql'
import { getInviteToAssessmentLink, isSet, uuidSmall } from '../../../helpers'
import { trackEvent } from '../../../helpers/analytics'
import { mailNotSentSpaceId } from '../../../helpers/invite-outcomes'
import {
  LIFE_CYCLE_STAGES,
  useLifecycleStageTracking
} from '../../../hooks/useLifecycleStageTracking'
import { useLocalStorage } from '../../../hooks/useLocalStorage'
import { useSpaceQuery } from '../../../hooks/useSpaceQuery'
import { useDiscover } from '../hooks/useDiscover'
import {
  MESSAGE_ROLES,
  MESSAGE_TYPES,
  sendSmartMessageSequence
} from '../utils'
import StartOptionCard from './intro/StartOptionCard'

export default function StartOptionsSection({ className }) {
  const { activeSpace } = useUserContext()
  const [invitationSpaceStorage, setInvitationSpaceStorage] = useLocalStorage(
    'invitationSpace',
    null
  )
  const { trackLifecycleStage } = useLifecycleStageTracking()

  const { data: invitationSpace } = useSpaceQuery({
    id: invitationSpaceStorage?.id
  })

  const isInvitationSpaceCompleted = invitationSpace?.progress === 100

  const { partner } = usePartnerContext()
  const { error } = useNotificationContext()
  const [isLoadingAssessment, setIsLoadingAssessment] = useState(false)
  const { invitation_email, email } = activeSpace.person
  const navigate = useNavigate()
  const [hideAssessmentIntro, setHideAssessmentIntro] = useState(() => {
    return localStorage.getItem('hideAssessmentIntro') === 'true'
  })
  const {
    setShowAssistant,
    showAssistant,
    sendMessageToAssistant,
    hideAssistant,
    setAssistantIsTyping,
    enableAssistant,
    setShowAssistantBackButton
  } = useDiscover()
  const { t } = useTranslation()

  // useEffect(() => {
  //   const registrationBrandDataStr = localStorage.getItem(
  //     'registrationBrandData'
  //   )
  //   const assistantIntroShown =
  //     localStorage.getItem('assistantIntroShown') === 'true'
  //   if (
  //     !firstname ||
  //     !lastname ||
  //     (registrationBrandDataStr && window.__aivy_registration_active) ||
  //     assistantIntroShown
  //   ) {
  //     return
  //   }
  //   setTimeout(() => {
  //     const firstname = activeSpace?.person?.firstname || ''
  //     enableAssistant()
  //     setShowAssistant(true)
  //     sendMessageToAssistant({
  //       id: 'discover-intro-welcome',
  //       description: t('discover.assistant.intro_page.description'),
  //       role: MESSAGE_ROLES.ASSISTANT,
  //       type: MESSAGE_TYPES.TEXT
  //     })
  //     localStorage.setItem('assistantIntroShown', 'true')
  //     setTimeout(() => {
  //       setAssistantFocus(false)
  //     }, 3000)
  //   }, 2500)
  // }, [])

  const getAssessmentMessages = useCallback(
    () => [
      {
        id: 'explore_strengths_1',
        title: t('discover.assistant.system_message.explore_strengths.1.title'),
        description: t(
          'discover.assistant.system_message.explore_strengths.1.description'
        )
      },
      {
        id: 'explore_strengths_2',
        title: t('discover.assistant.system_message.explore_strengths.2.title'),
        description: t(
          'discover.assistant.system_message.explore_strengths.2.description'
        )
      }
      // {
      //   id: 'explore_strengths_3',
      //   title: t('discover.assistant.system_message.explore_strengths.3.title'),
      //   description: t(
      //     'discover.assistant.system_message.explore_strengths.3.description'
      //   )
      // }
    ],
    [t]
  )

  const trackOnboardingStarted = () => {
    const storageKey = 'onboardingStartedTracked'
    if (localStorage.getItem(storageKey) === 'true') return

    trackLifecycleStage({
      lifecycle_stage: LIFE_CYCLE_STAGES.ONBOARDING_STARTED,
      lifecycle_stage_before: LIFE_CYCLE_STAGES.PLG_CREATED,
      aivy_form_id: partner.aivy_form_id,
      lead_progress: (2 / 6).toFixed(2),
      partner_id: partner.id
    })
    localStorage.setItem(storageKey, 'true')
  }

  const openAssessment = (invitationLink) => {
    if (!isSet(invitationLink)) return

    trackEvent(trackEvents.DISCOVER_INTRO_CLICK_START_ASSESSMENT_CTA)

    window.open(invitationLink, 'assessmentWindow', 'width=600,height=800')
    /**
     * window.opener.postMessage("assessment-closed", "*");
     * window.close();
     */
  }

  const handleOnClickPrimary = async () => {
    trackOnboardingStarted()
    trackEvent(trackEvents.DISCOVER_INTRO_CLICK_START_ASSESSMENT)
    enableAssistant()
    setShowAssistantBackButton(true)
    let space

    await sendSmartMessageSequence({
      setIsTyping: setAssistantIsTyping,
      messages: getAssessmentMessages().map((msg) => ({
        fn: () =>
          sendMessageToAssistant({
            id: msg.id,
            title: msg.title,
            description: msg.description,
            role: MESSAGE_ROLES.ASSISTANT,
            type: MESSAGE_TYPES.TEXT
          }),
        textLength: msg.title.length + msg.description.length
      }))
    })

    const variables = {
      language: 'de',
      partner_id: partner.id,
      partner_user_id: uuidSmall(),
      send_mail: true,
      email_template: 'invite',
      email: invitation_email || email
    }

    try {
      setIsLoadingAssessment(true)
      trackEvent(trackEvents.DISCOVER_INTRO_CLICK_START_ASSESSMENT)

      if (isSet(invitationSpaceStorage)) {
        space = invitationSpaceStorage
      } else {
        space = await query({ query: 'inviteTalent', variables })
      }

      setInvitationSpaceStorage(space)
    } catch (err) {
      // Der Space steht, nur die Mail ging nicht raus. Den Link gibt es hier
      // ohnehin im Chat, also geht es mit diesem Space weiter.
      const unsentSpaceId = mailNotSentSpaceId(err)

      if (unsentSpaceId) {
        space = { id: unsentSpaceId, language: variables.language }
        setInvitationSpaceStorage(space)
      } else {
        error(err)
      }
    } finally {
      setIsLoadingAssessment(false)
    }

    // Ohne Space gibt es keinen Link, der Fehler steht schon im Hinweis
    if (!space) return

    const invitationLink = getInviteToAssessmentLink({
      space_id: space.id,
      language: space.language || 'de'
    })

    await sendSmartMessageSequence({
      setIsTyping: setAssistantIsTyping,
      messages: [
        {
          fn: () =>
            sendMessageToAssistant({
              id: 'explore_strengths_4',
              title: t(
                'discover.assistant.system_message.explore_strengths.4.title'
              ),
              description: t(
                'discover.assistant.system_message.explore_strengths.4.description'
              ),
              cta: {
                title: t(
                  'discover.assistant.system_message.explore_strengths.4.cta'
                ),
                onClick: () => openAssessment(invitationLink)
              },
              withQrCode: invitationLink,
              role: MESSAGE_ROLES.ASSISTANT,
              type: MESSAGE_TYPES.TEXT
            }),
          textLength:
            t('discover.assistant.system_message.explore_strengths.4.title')
              .length +
            t(
              'discover.assistant.system_message.explore_strengths.4.description'
            ).length
        },
        {
          fn: () =>
            sendMessageToAssistant({
              id: 'explore_strengths_5',
              title: t(
                'discover.assistant.system_message.explore_strengths.5.title'
              ),
              description: t(
                'discover.assistant.system_message.explore_strengths.5.description'
              ),
              role: MESSAGE_ROLES.ASSISTANT,
              type: MESSAGE_TYPES.TEXT
            }),
          textLength:
            t('discover.assistant.system_message.explore_strengths.5.title')
              .length +
            t(
              'discover.assistant.system_message.explore_strengths.5.description'
            ).length
        }
      ]
    })
  }

  const handleHideIntro = () => {
    setHideAssessmentIntro(true)
    trackEvent(trackEvents.DISCOVER_INTRO_ASSESSMENT_HIDE_INTRO)
    localStorage.setItem('hideAssessmentIntro', 'true')
  }

  const handleResetIntro = () => {
    setHideAssessmentIntro(false)
    trackEvent(trackEvents.DISCOVER_INTRO_ASSESSMENT_RESET_INTRO)
    localStorage.removeItem('hideAssessmentIntro')
  }

  const handleOnClickSecondary = () => {
    setShowAssistant(!showAssistant)
    trackOnboardingStarted()
    enableAssistant()
    setShowAssistantBackButton(true)

    sendSmartMessageSequence({
      messages: [
        {
          fn: () =>
            sendMessageToAssistant({
              id: 'welcome-message',
              title: t('discover.assistant.system_message.intro.title'),
              description: t(
                'discover.assistant.system_message.intro.description'
              ),
              role: MESSAGE_ROLES.ASSISTANT,
              type: MESSAGE_TYPES.TEXT
            }),
          textLength:
            t('discover.assistant.system_message.intro.title').length +
            t('discover.assistant.system_message.intro.description').length
        },
        {
          fn: () =>
            sendMessageToAssistant({
              id: 'welcome-option-message',
              type: MESSAGE_TYPES.OPTION,
              role: MESSAGE_ROLES.ASSISTANT,
              options: [
                {
                  title: t('discover.assistant.radio_group.options.1.title'),
                  description: t(
                    'discover.assistant.radio_group.options.1.description'
                  ),
                  recommended: true,
                  onClick: () => {
                    trackEvent(
                      trackEvents.DISCOVER_CLICK_CHECK_FIT_WITH_POSITION
                    )
                    setTimeout(() => {
                      hideAssistant()
                      navigate('/discover')
                    }, 300)
                  }
                },
                {
                  title: t('discover.assistant.radio_group.options.2.title'),
                  description: t(
                    'discover.assistant.radio_group.options.2.description'
                  ),
                  recommended: false,
                  disabled: true,
                  onClick: () => {
                    trackEvent(trackEvents.DISCOVER_CLICK_ANALYZE_TALENT)

                    setTimeout(() => {
                      hideAssistant()
                      navigate('/discover/?option=talent_profile')
                    }, 300)
                  }
                }
              ]
            }),
          textLength:
            t('discover.assistant.radio_group.options.1.title').length +
            t('discover.assistant.radio_group.options.1.description').length +
            t('discover.assistant.radio_group.options.2.title').length +
            t('discover.assistant.radio_group.options.2.description').length
        }
      ],
      setIsTyping: setAssistantIsTyping
    })
  }

  return (
    <div className={clsx('pt-6', className)}>
      <h2 className='text-2xl font-semibold text-darkBlue'>
        {t('discover.start_options_section.title')}
      </h2>
      <div className='mt-6'>
        <StartOptionCard
          title={t('discover.start_options_section.cards.evaluation.title')}
          // badge={isInvitationSpaceCompleted}
          badge={true}
          description={t(
            'discover.start_options_section.cards.evaluation.description'
          )}
          primaryButtonLabel={t(
            'discover.start_options_section.cards.evaluation.secondary_button_label'
          )}
          onClickPrimary={handleOnClickSecondary}
          image={EvaluationsIllustration}
        />
        {!hideAssessmentIntro && (
          <StartOptionCard
            className={'mt-4'}
            title={t('discover.start_options_section.cards.assessment.title')}
            // badge={!isInvitationSpaceCompleted}
            badge={false}
            description={t(
              'discover.start_options_section.cards.assessment.description'
            )}
            secondaryButtonLabel={t(
              'discover.start_options_section.cards.assessment.primary_button_label'
            )}
            onClickSecondary={handleOnClickPrimary}
            isLoadingSecondary={isLoadingAssessment}
            textButtonLabel={
              isInvitationSpaceCompleted
                ? t('discover.start_options_section.cards.assessment.try_again')
                : t(
                    'discover.start_options_section.cards.assessment.text_button_label'
                  )
            }
            onClickText={
              isInvitationSpaceCompleted
                ? handleOnClickPrimary
                : handleHideIntro
            }
            image={AssessmentIllustration}
            completed={isInvitationSpaceCompleted}
          />
        )}
        {hideAssessmentIntro && (
          <Button.Text
            text={t(
              'discover.start_options_section.cards.assessment.test_again'
            )}
            onClick={handleResetIntro}
          />
        )}
      </div>
    </div>
  )
}

StartOptionsSection.propTypes = {
  className: PropTypes.string
}
